import { ChangeEvent, useEffect, useState } from "react";
import UserTypesEnum from "../../../../models/UserTypesEnum";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import TitleTagEnum from "../../../../models/TitleTagEnum";
import * as _ from "lodash-es";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwButton from "../../../shared/OwButton/OwButton";
import { Socket } from "socket.io-client";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IUpdateSupplierSettingsDTO from "../../../../models/DTO/IUpdateSupplierSettingsDTO";
import IOnUpdateSupplierSettingsDTO from "../../../../models/DTO/IOnUpdateSupplierSettingsDTO";
import INotification from "../../../../models/INotification";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import "./EditScreenOptions.scss";
import { sortBy } from "lodash-es";
import ScreenOptionsService from "../../../../services/ScreenOptionsService";
import ScreenOptionsEnum from "../../../../models/ScreenOptionsEnum";

type Props = {
    closeFn: () => void;
    socket: Socket;
    notifyFn: (data: any, notification: INotification) => void;
};

export const EditScreenOptions = (props: Props) => {
    const { supplierModel, userModel } = useAppContextNonNull();

    let existingScreenOptions: any = {};

    if (_.isString(supplierModel.settings)) {
        const parsedSetings = JSON.parse(supplierModel.settings);

        existingScreenOptions = parsedSetings?.screen_options || {};
    } else {
        existingScreenOptions = supplierModel.settings?.screen_options || {};
    }

    // Populate missing screens with ViewAccessService defaults
    const screenKeys = Object.values(ScreenOptionsEnum);

    const initializedScreenOptions = { ...existingScreenOptions };
    for (const screenKey of screenKeys) {
        if (!initializedScreenOptions[screenKey]) {
            initializedScreenOptions[screenKey] = ScreenOptionsService.getDefaultScreenOptions(screenKey);
        }
    }

    const [screenOptions, setScreenOptions] = useState(initializedScreenOptions);
    const [saveActive, setSaveActive] = useState(false);
    const [selectedScreen, setSelectedScreen] = useState<ScreenOptionsEnum>(ScreenOptionsEnum.ORDERS);

    useEffect(() => {
        props.socket.on(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);

        return () => {
            props.socket.removeListener(EndpointsEnum.SupplierOnUpdateSupplierSettings, onUpdateSupplierSettings);
        };
    }, []);

    const updateSupplierSettings = () => {
        const data: IUpdateSupplierSettingsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            // @ts-expect-error
            settings: {
                screen_options: screenOptions,
            },
        };

        props.socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, data);

        setSaveActive(true);
    };

    const onUpdateSupplierSettings = (data: IOnUpdateSupplierSettingsDTO) => {
        setSaveActive(false);

        const notification: INotification = {
            title: "successfully_updated_settings",
            position: NotificationPositionEnum.bc,
            level: NotificationLevelEnum.success,
        };

        props.notifyFn(null, notification);

        props.closeFn();
    };

    const getScreens = () => {
        const defaultScreens = [
            { key: ScreenOptionsEnum.ORDERS, label: "orders" },
            { key: ScreenOptionsEnum.ORDER_ENTRY, label: "order_entry" },
            { key: ScreenOptionsEnum.CATALOGS, label: "catalogs" },
            { key: ScreenOptionsEnum.ACCOUNTS, label: "accounts" },
            { key: ScreenOptionsEnum.ACCOUNT_USERS, label: "account_users" },
        ];

        if (supplierModel.has_purchase_orders) {
            defaultScreens.push({ key: ScreenOptionsEnum.PURCHASE_ORDERS, label: "purchase_orders" });
        }

        if (supplierModel.has_templates) {
            defaultScreens.push({ key: ScreenOptionsEnum.TEMPLATES, label: "templates" });
        }

        if (supplierModel.has_shipments) {
            defaultScreens.push({ key: ScreenOptionsEnum.SHIPPING, label: "shipping" });
        }

        if (supplierModel.has_inventory) {
            defaultScreens.push({ key: ScreenOptionsEnum.INVENTORY, label: "inventory" });
        }

        if (supplierModel.has_quotes) {
            defaultScreens.push({ key: ScreenOptionsEnum.QUOTES, label: "quotes" });
        }

        if (supplierModel.has_pick_list_management) {
            defaultScreens.push({ key: ScreenOptionsEnum.PICK_LISTS, label: "pick_lists" });
            defaultScreens.push({ key: ScreenOptionsEnum.PACKING_LISTS, label: "packing_lists" });
        }

        return defaultScreens;
    };

    const screens = sortBy(getScreens(), (screen) => screen.label);

    const userTypes = [
        { value: UserTypesEnum.ADMIN, title: "admin" },
        { value: UserTypesEnum.SALES, title: "sales" },
        { value: UserTypesEnum.ORDER_MGMT, title: "order_mgmt" },
        { value: UserTypesEnum.SHIP_MGMT, title: "ship_mgmt" },
        { value: UserTypesEnum.INVENTORY_MGMT, title: "inventory_mgmt" },
        // {value: UserTypesEnum.SUPPORT, title: "support"},
        // {value: UserTypesEnum.MANAGEMENT, title: "management"},
    ];

    const updateScreenOption = (screen: string, userType: UserTypesEnum, type: "READ" | "EDIT", e: ChangeEvent<any>) => {
        const value = e.target.type === "checked" ? e.target.checked : e.target.value;

        const updatedScreenOptions: any = { ...screenOptions };

        if (!updatedScreenOptions[screen]) {
            updatedScreenOptions[screen] = {
                [UserTypesEnum.ADMIN]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.SALES]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.ORDER_MGMT]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.SHIP_MGMT]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.INVENTORY_MGMT]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.SUPPORT]: {
                    READ: false,
                    EDIT: false,
                },
                [UserTypesEnum.MANAGEMENT]: {
                    READ: false,
                    EDIT: false,
                },
            };
        }

        updatedScreenOptions[screen][userType][type] = value;

        setScreenOptions(updatedScreenOptions);
    };

    const handleScreenSelect = (screenKey: ScreenOptionsEnum) => {
        setSelectedScreen(screenKey);
    };

    const resetToDefaults = () => {
        const defaultOptions = ScreenOptionsService.getDefaultScreenOptions(selectedScreen);

        const updatedScreenOptions: any = { ...screenOptions };
        updatedScreenOptions[selectedScreen] = defaultOptions;

        setScreenOptions(updatedScreenOptions);
    };

    const saveOptions = () => {
        updateSupplierSettings();
    };

    return (
        <div className={"EditScreenOptions"}>
            <div className={"d-flex align-items-center"}>
                <OwTitle title={"edit_screen_options"} tag={TitleTagEnum.H3} />
                <div className={"ms-4"}>
                    <OwButton title={"save"} fn={saveOptions} disableOnActive={true} active={saveActive} />
                </div>
            </div>

            <div className={"d-flex flex-row gap-3 mt-3"}>
                {/* Sidebar navigation */}
                <div className={"EditScreenOptions__sidebar"}>
                    {screens.map((screen) => (
                        <div
                            key={screen.key}
                            className={`EditScreenOptions__sidebar__item ${selectedScreen === screen.key ? "active" : ""}`}
                            onClick={() => handleScreenSelect(screen.key)}>
                            <OwLabel title={screen.label} />
                        </div>
                    ))}
                </div>

                {/* Main content area */}
                <div className={"EditScreenOptions__content flex-grow-1"}>
                    <div className={"EditScreenOptions__option"}>
                        <div className={"EditScreenOptions__option__title"}>
                            <div className={"d-flex align-items-center w-100"}>
                                <OwTitle
                                    tag={TitleTagEnum.H4}
                                    title={screens.find((s) => s.key === selectedScreen)?.label || selectedScreen}
                                />
                                <div className={"ms-2"}>
                                    <OwButton title={"reset_to_defaults"} fn={resetToDefaults} className={"btn-danger btn-sm"} />
                                </div>
                            </div>
                        </div>
                        <div className={"d-flex flex-row gap-5"}>
                            {userTypes.map((userType, indexTwo) => {
                                const isAdmin = userType.value === UserTypesEnum.ADMIN;
                                return (
                                    <div key={"_" + indexTwo} className={"mb-3"}>
                                        <div>
                                            <OwLabel title={userType.title} />
                                        </div>
                                        <div>
                                            <OwCheckbox
                                                label={"read"}
                                                name={selectedScreen + "_read"}
                                                fn={(e) => updateScreenOption(selectedScreen, userType.value, "READ", e)}
                                                truthyVal={true}
                                                falsyVal={false}
                                                checked={
                                                    isAdmin ||
                                                    (screenOptions[selectedScreen] &&
                                                        screenOptions[selectedScreen][userType.value] &&
                                                        screenOptions[selectedScreen][userType.value].READ)
                                                }
                                                disabled={isAdmin}
                                            />
                                        </div>
                                        <div>
                                            <OwCheckbox
                                                label={"edit"}
                                                name={selectedScreen + "_edit"}
                                                fn={(e) => updateScreenOption(selectedScreen, userType.value, "EDIT", e)}
                                                truthyVal={true}
                                                falsyVal={false}
                                                checked={
                                                    isAdmin ||
                                                    (screenOptions[selectedScreen] &&
                                                        screenOptions[selectedScreen][userType.value] &&
                                                        screenOptions[selectedScreen][userType.value].EDIT)
                                                }
                                                disabled={isAdmin}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
